/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IStoryBlokAnnouncement,
  IStoryBlokAnnouncementBanner,
  IStoryBlokCookieBar,
  IStoryBlokNewsletterSignUpModal,
  IStoryBlokSeo,
} from "apps/website/components/page/api.types";
import {
  IStoryBlokAnnouncementBannerProps,
} from "apps/website/components/storyblok/page/StoryBlokAnnouncementBanner";
import { IHeaderLink } from "apps/website/components/base/Button/Button.map";
import {
  HEADER_LINKS_DEFAULT,
  HEADER_LINKS_BREEDER,
  IHeaderLinks,
  HEADER_LINKS_META,
  HEADER_LINKS_FRESH_FEAST_TRIAL,
  HEADER_LINKS_SIGNUP_FLOW_V2_AC,
  HEADER_LINKS_SIGNUP_FLOW_V2_LANDING,
  HEADER_LINKS_SCOOP_PLANET,
} from "apps/website/components/page/Header/Header.map";

import { makePageTags } from "../website/head";
import { renderStoryBlokRichText } from "../storyblok/text";
import { ImageMax, getStoryBlokImage } from "../storyblok/image";
import { HeaderScript, ILayoutData } from "../../layouts/layouts.map";

export const getHeadData = (
  seo: IStoryBlokSeo | undefined,
  hideFromSearch: boolean | undefined,
  pageLastBuilt: string | undefined,
  fallbackTags: IStoryBlokSeo | undefined,
) => ({
  seo: seo ? makePageTags(seo) : undefined,
  hideFromSearch: hideFromSearch || false,
  lastBuilt: pageLastBuilt,
  fallbackTags: fallbackTags ? makePageTags(fallbackTags) : undefined,
});

export const getAnnouncementBannerData = (
  pageAnnouncements: IStoryBlokAnnouncement[],
  defaultAnnouncements: IStoryBlokAnnouncementBanner,
): IStoryBlokAnnouncementBannerProps => ({
  pageAnnouncements,
  defaultAnnouncements,
});

export const getCookieBannerData = (
  banner: IStoryBlokCookieBar,
) => ({
  body: renderStoryBlokRichText(banner.body),
  cta: { id: banner.cta[0]._uid, ...banner.cta[0] },
});

export const getNewsletterData = (
  newsletter: IStoryBlokNewsletterSignUpModal,
) => ({
  title: newsletter.title,
  completeTitle: newsletter.complete_title,
  body: newsletter.body,
  completeBody: newsletter.complete_body,
  buttonText: newsletter.button_text,
  theme: newsletter.theme,
  image: getStoryBlokImage(newsletter.image, true, ImageMax.CONTAINER_SM),
});

/**
 * Formats a discount code or query string into a query string format.
 * Safely checks if the input is already formatted (contains "discount_code=" or "&")
 * to avoid double-formatting.
 */
const formatDiscountCodeQuery = (discountCodeOrQuery: string): string => {
  if (discountCodeOrQuery.includes("discount_code=") || discountCodeOrQuery.includes("&")) {
    return discountCodeOrQuery;
  }
  return `discount_code=${discountCodeOrQuery}`;
};

const mapLinksWithAssociatedDiscount = (links: IHeaderLink[], associatedDiscount: string) => links.map((link) => ({
  ...link,
  to: link.withAssociatedDiscount ? `${link.to}?${associatedDiscount}` : link.to,
}));

const getHeaderLinksWithAssociatedDiscount = (headerLinks: IHeaderLinks, associatedDiscount: string | undefined) => {
  if (!associatedDiscount) { return headerLinks; }
  return {
    primaryLinks: mapLinksWithAssociatedDiscount(headerLinks.primaryLinks, associatedDiscount),
    secondaryLinks: mapLinksWithAssociatedDiscount(headerLinks.secondaryLinks, associatedDiscount),
    authLinks: mapLinksWithAssociatedDiscount(headerLinks.authLinks, associatedDiscount),
    guestLinks: mapLinksWithAssociatedDiscount(headerLinks.guestLinks, associatedDiscount),
  };
};

const getHeaderLinksForStory = (story: any, associatedDiscount: string | undefined) => {
  if (story?.isFreshFeastTrialPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_FRESH_FEAST_TRIAL, associatedDiscount);
  }
  if (story?.isSignupFlowV2AC) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_SIGNUP_FLOW_V2_AC, associatedDiscount);
  }
  if (story?.isSignupFlowV2Landing) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_SIGNUP_FLOW_V2_LANDING, associatedDiscount);
  }

  if (story?.isMetaSignUpFunnelPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_META, associatedDiscount);
  }

  if (story?.main?.content?.header_navigation === "breeder") {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_BREEDER, associatedDiscount);
  }

  if (story?.main?.content?.header_navigation === "scoop-planet") {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_SCOOP_PLANET, associatedDiscount);
  }

  return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_DEFAULT, associatedDiscount);
};

const getAnnouncementBannerIfEnabled = (story: any) => {
  if (!story?.main?.content?.announcements && !story?.announcementBanner) {
    return null;
  }
  return getAnnouncementBannerData(story.main?.content?.announcements, story.announcementBanner);
};

// @ts-ignore
export const getDefaultLayoutData = (
  story?: any,
  lastBuilt?: string,
  scripts: HeaderScript[] = [ "trustpilot" ],
  pageSlug?: string,
  discountCodeOverrides?: Record<string, string>,
): ILayoutData => { // eslint-disable-line
  // First check Storyblok's associated_discount_code (takes precedence)
  let associatedDiscount = story?.main?.content?.associated_discount_code;

  // If no Storyblok discount code and this is a signup flow v2 landing page:
  // Check for override if pageSlug is provided, otherwise default to TRY20
  if (!associatedDiscount && story?.isSignupFlowV2Landing) {
    if (pageSlug && discountCodeOverrides?.[pageSlug]) {
      associatedDiscount = discountCodeOverrides[pageSlug];
    } else {
      associatedDiscount = "TRY20";
    }
  }

  // Format the discount code query string once here to avoid duplication
  const formattedAssociatedDiscount = associatedDiscount
    ? formatDiscountCodeQuery(associatedDiscount)
    : undefined;

  return {
    headerLinks: getHeaderLinksForStory(story, formattedAssociatedDiscount),
    head: story?.main && getHeadData(
      story.main.content.seo,
      story.main.content.hide_from_search_engines,
      lastBuilt,
      story.fallbackTags,
    ),
    scripts,
    announcementBanner: getAnnouncementBannerIfEnabled(story),
    cookieBanner: story?.cookieBar && getCookieBannerData(story.cookieBar),
    hideCookieBanner: story?.main?.content?.hide_cookie_bar,
    hideAnnouncementBanner: story?.main?.content?.hide_announcements || false,
    newsletterModal: story?.newsletterSignUpModal && getNewsletterData(story.newsletterSignUpModal),
    lastBuilt,
    jsonLd: story?.jsonLd,
    isMetaSignUpFunnelPage: story?.isMetaSignUpFunnelPage,
    isFreshFeastTrialPage: story?.isFreshFeastTrialPage,
    isSignupFlowV2AC: story?.isSignupFlowV2AC,
    isSignupFlowV2Landing: story?.isSignupFlowV2Landing,
    associatedDiscount: formattedAssociatedDiscount,
  };
};
